import React, { useEffect, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import * as S from '../Advances/AdvancesStyles';
import { getPendingLeaves, approveRejectLeave } from '../../../../services/attendanceService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const fmtDate = d => {
  if (!d) return '—';
  const parts = String(d).split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const LEAVE_TYPE_LABEL = { CASUAL: 'Casual', SICK: 'Sick', EARNED: 'Earned', UNPAID: 'Unpaid' };

const LeaveApprovalTab = () => {
  const [loading, setLoading] = useState(true);
  const [leaves, setLeaves] = useState([]);

  const load = async () => {
    setLoading(true);
    setLeaves(await getPendingLeaves());
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleApprove = (leave) => {
    Swal.fire({
      title: 'Approve this leave?',
      text: `${leave.user_id?.name || 'Staff'} · ${LEAVE_TYPE_LABEL[leave.leave_type] || leave.leave_type} · ${leave.total_days} day(s)`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve',
      confirmButtonColor: '#16a34a',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await approveRejectLeave(leave._id, 'APPROVED', '');
        toast.success('Leave approved.');
        load();
      } catch (err) {
        console.error('Approve leave failed:', err);
        toast.error('Failed to approve leave.');
      }
    });
  };

  const handleReject = (leave) => {
    Swal.fire({
      title: 'Reason for rejection:',
      input: 'text',
      inputPlaceholder: 'Enter reason…',
      showCancelButton: true,
      confirmButtonText: 'Confirm Rejection',
      confirmButtonColor: '#D5372A',
    }).then(async (result) => {
      if (result.value === undefined || result.value === null) return;
      try {
        await approveRejectLeave(leave._id, 'REJECTED', result.value);
        toast.info('Leave rejected.');
        load();
      } catch (err) {
        console.error('Reject leave failed:', err);
        toast.error('Failed to reject leave.');
      }
    });
  };

  const columns = [
    {
      dataField: 'applied_at',
      text: 'Applied',
      sort: true,
      formatter: (cell) => new Date(cell).toLocaleDateString('en-IN'),
    },
    {
      dataField: 'user_id',
      text: 'Staff',
      formatter: (cell) => <span className="member-link">{cell?.name || 'Staff'}</span>,
    },
    {
      dataField: 'reporting_to',
      text: 'Reporting To',
      formatter: (cell) =>
        cell?.name ? <S.Chip>{cell.name}</S.Chip> : <span className="rowmuted">—</span>,
    },
    {
      dataField: 'leave_type',
      text: 'Type',
      formatter: (cell) => <S.Chip blue>{LEAVE_TYPE_LABEL[cell] || cell}</S.Chip>,
    },
    {
      dataField: 'date_from',
      text: 'Dates',
      formatter: (cell, row) => `${fmtDate(row.date_from)} → ${fmtDate(row.date_to)}`,
    },
    { dataField: 'total_days', text: 'Days', sort: true },
    {
      dataField: 'reason',
      text: 'Reason',
      formatter: (cell) => <span className="rowmuted">{cell || '—'}</span>,
    },
    {
      dataField: 'decision',
      text: 'Decision',
      isDummyField: true,
      formatter: (cell, leave) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <S.ActionIconBtn variant="approve" title="Approve" onClick={() => handleApprove(leave)}>
            ✓
          </S.ActionIconBtn>
          <S.ActionIconBtn variant="reject" title="Reject" onClick={() => handleReject(leave)}>
            ✕
          </S.ActionIconBtn>
        </div>
      ),
    },
  ];

  const pending = leaves.filter(l => l.status === 'PENDING');

  return (
    <div>
      <S.CardBox style={{ marginBottom: 14 }}>
        <strong style={{ fontSize: 16 }}>{pending.length}</strong> leave request(s) waiting on approval
      </S.CardBox>

      <S.TableWrap>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading…</div>
        ) : (
          <BootstrapTable
            keyField="_id"
            data={pending}
            columns={columns}
            bordered={false}
            hover
            noDataIndication={() => (
              <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>
                Nothing pending — you're all caught up.
              </div>
            )}
            pagination={paginationFactory({ sizePerPage: 50, sizePerPageList: [50, 100, 200], showTotal: true })}
          />
        )}
      </S.TableWrap>
    </div>
  );
};

export default LeaveApprovalTab;
